import { useEffect, useLayoutEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { cn } from '../utils/utils';
import { isAutomated, useReducedMotion } from './useReducedMotion';

/** `--ease-out` from the app's globals.css, the same curve `useTabEnter` uses. */
const EASE_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)';
const DURATION = '250ms';

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

function sameBox(a: Box | null, b: Box | null) {
  if (a === null || b === null) return a === b;
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

/**
 * One active-tab background that glides to whichever tab is selected, instead
 * of each button painting its own and the highlight jumping.
 *
 * The container gets `containerRef` and must be `relative`; the active button
 * carries `data-pill-active='true'`; `pill` is the ready-made element to render
 * first inside the container, drawn BEHIND the buttons (buttons
 * `relative z-10`). Measured with offsetLeft/offsetTop so it tracks
 * the button in the container's own coordinates, and re-measured on resize,
 * because `LessonTabs` buttons are `flex-1` and change width with the panel.
 *
 * No slide on first paint (the pill appears in place), under reduced motion,
 * or under automation (`navigator.webdriver`): a story that ends on a tab
 * switch is scanned by axe at once, and white label text over a pill still in
 * transit fails colour-contrast — the same reason `useTabEnter` skips there.
 */
export function useSlidingPill<C extends HTMLElement = HTMLDivElement>(
  activeKey: string,
  /** Shape and fill of the pill, e.g. `rounded-full bg-primary shadow-sm`. */
  pillClassName: string
) {
  // A callback ref held in STATE, not a ref object: the effect below must
  // re-run when the container first appears. With a ref object it ran once on
  // the owner's mount, and a tab bar that mounts LATER (inside a modal that
  // renders its body only when open) was never measured, so the active label
  // (white, meant for the pill) sat invisible until the tab changed.
  const [container, containerRef] = useState<C | null>(null);
  const [box, setBox] = useState<Box | null>(null);
  const [ready, setReady] = useState(false);
  const reduced = useReducedMotion();

  useLayoutEffect(() => {
    if (!container) return;
    const measure = () => {
      const el = container.querySelector<HTMLElement>(
        '[data-pill-active="true"]'
      );
      const next = el
        ? {
            x: el.offsetLeft,
            y: el.offsetTop,
            w: el.offsetWidth,
            h: el.offsetHeight,
          }
        : null;
      // Keep the previous object when nothing moved. The observer fires on
      // every resize (and once on attach); a fresh object each time re-rendered
      // the whole tab body, which remounts the Text tab's citation chips and
      // closed an open hover card under the reader's pointer.
      setBox(prev => (sameBox(prev, next) ? prev : next));
    };
    measure();
    // Absent in jsdom (and very old browsers): measure once, skip resize tracking.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [activeKey, container]);

  // Turn the transition on only after the pill has painted in place once.
  const hasBox = box !== null;
  useEffect(() => {
    if (!hasBox || ready) return;
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, [hasBox, ready]);

  const animate = ready && !reduced && !isAutomated();
  const pillStyle: CSSProperties = box
    ? {
        width: box.w,
        height: box.h,
        transform: `translate(${box.x}px, ${box.y}px)`,
        transition: animate
          ? `transform ${DURATION} ${EASE_OUT}, width ${DURATION} ${EASE_OUT}`
          : 'none',
      }
    : { opacity: 0 };

  const pill = <SlidingPill style={pillStyle} className={pillClassName} />;
  return { containerRef, pill };
}

/** The pill itself: decorative, click-through, drawn under the buttons. */
function SlidingPill({
  style,
  className,
}: {
  style: CSSProperties;
  className: string;
}) {
  return (
    <div
      aria-hidden
      className={cn('pointer-events-none absolute top-0 left-0', className)}
      style={style}
    />
  );
}
